<template>
  <transition
    v-if="active"
    name="modal"
  >
    <div class="upsell-modal-component-container" data-test="upsell-modal">
      <div class="content">
        <div
          class="close"
          @click="hide()"
        >
          <icon
            name="cross"
            variant="b3b3b3"
          />
        </div>
        <div class="title">
          {{ heading }}
        </div>
        <div
          v-if="isSubscription && trialEnabled"
          class="subtitle"
        >
          Get {{ trialDurationText }} of free access to all features, including:
        </div>
        <div
          class="list"
          v-html="selectedPurchaseOrder.descriptionHtml"
        />
        <div class="options">
          <template v-for="(purchaseOrder, index) in purchaseOrders">
            <div
              v-if="index > 0"
              :key="`spacer-${index}`"
              class="spacer"
            />
            <div
              :key="`box-${index}`"
              :class="{preferred: isPreferred(purchaseOrder), selected: isSelected(purchaseOrder)}"
              @click="boxClick(purchaseOrder, index)"
            >
              <div class="name">
                {{ purchaseOrder.name }}
              </div>
              <div class="price">
                <div>${{ price(purchaseOrder) }}</div>
                <div v-if="isSubscription">
                  {{ timeframe(purchaseOrder) }}
                </div>
              </div>
              <purchase-form
                ref="submitForm"
                :action="legacyLink"
                :partner-ref="partnerRef"
                :purchase-order="purchaseOrder"
                :sc="sc"
                :user="user"
              >
                <button
                  :class="[purchaseOrder.purchaseOrderType]"
                  type="button"
                >
                  {{ unlockText(purchaseOrder) }}
                </button>
              </purchase-form>
            </div>
          </template>
        </div>
        <div
          v-if="isSubscription && trialEnabled"
          class="disclaimers"
        >
          <div class="semi-bold-disclaimer">
            No risk - cancel anytime
          </div>
          <div>We’re confident you’ll love our service but we want you to find out for yourself with {{ trialDurationText }} free days. In {{ trialDurationText }}, you’ll be auto-enrolled in the membership plan shown above. No commitments, cancel anytime!</div>
          <div>If you’re not satisfied in the first {{ trialDurationText }} days... cancel it! You won’t be charged if cancelled during your trial.</div>
        </div>
        <div
          v-else-if="isSubscription"
          class="disclaimers"
        >
          <div>Recurring charge will automatically apply. You may cancel anytime.</div>
          <div>You will be charged immediately for the plan you choose above. After that, a recurring charge will automatically apply, timed according to the plan you select. You may cancel at any time in your profile.</div>
          <div>By clicking a ‘Subscribe’ button, you agree to authorize the recurring charge.</div>
        </div>
        <div
          v-else-if="isTieredPricing"
          class="disclaimers"
        >
          <button @click="triggerSubmit(selectedPurchaseOrderIndex)">
            Buy {{ selectedPurchaseOrder.name }} Now
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import PurchaseForm from '@components/singletons/purchaseForm';
import Icon from '@components/singletons/icon';
import TrialMixin from '@/mixins/trial';

import { legacyFormLink } from '@/utils';

export default {
  components: { PurchaseForm, Icon },
  mixins: [TrialMixin],
  data() {
    return {
      active: false,
      partnerRef: undefined,
      sc: undefined,
      selected: {},
      type: undefined,
    };
  },
  computed: {
    heading() {
      if (this.isSimulation) return 'Unlock Today';
      if (this.isSubscription) {
        if (this.trialEnabled) {
          return 'Start Your Free Trial';
        } else {
          return 'Subscribe Today';
        }
      }
      if (this.isTieredPricing) return 'Unlock Today';
    },
    isSimulation() {
      return this.type === 'Test Simulation';
    },
    isSubscription() {
      return this.type === 'Subscription';
    },
    isTieredPricing() {
      return this.type === 'Tiered Pricing';
    },
    lastPurchaseOrder() {
      return this.purchaseOrders.slice(-1).pop() || {};
    },
    legacyLink() {
      return legacyFormLink(this.$store);
    },
    listener() {
      return this.$store.getters['ui/upsellModalStatus'];
    },
    preferredPurchaseOrder() {
      const pv = this.$store.getters['productVars/get']['preferred_subs_purchase_order_id'];
      let preferred = undefined;
      if (pv) {
        const id = parseInt(pv.value);
        preferred = this.purchaseOrders.find(po => po.id === id);
      }

      return preferred || {};
    },
    purchaseOrders() {
      const list = this.$store.getters['purchaseOrders/web'].filter(po => po.purchaseOrderType === this.type);
      if (this.isTieredPricing) {
        const purchased = this.user.purchaseOrders.map(id => list.find(po => po.id === id)).sort((a, b) => b.order - a.order);
        return list.filter(po => po.parentId === purchased[0].id);
      } else if (this.isSubscription) {
        const userClassificationId = this.$store.getters['session/currentUser']?.classificationId;
        if (userClassificationId) {
          return list.filter(po => po.classificationId === userClassificationId);
        } else {
          return list.filter(po => !po.classificationId);
        }
      }

      return list;
    },
    selectedPurchaseOrder() {
      if (this.selected.id) {
        return this.selected;
      } else if (this.preferredPurchaseOrder.id) {
        return this.preferredPurchaseOrder;
      } else {
        return this.lastPurchaseOrder;
      }
    },
    selectedPurchaseOrderIndex() {
      return this.purchaseOrders.map(po => po.id).indexOf(this.selectedPurchaseOrder.id);
    },
    user() {
      return this.$store.getters['session/currentUser'] || { authenticationToken: '', email: '', purchaseOrders: [] };
    },
  },
  created() {
    this.$watch('listener', (newValue, oldValue) => {
      if (newValue && !oldValue) {
        this.toggle();
      }
    });
    this.$watch('$route', (newRoute, oldRoute) => {
      if (this.active) {
        if (newRoute !== oldRoute) {
          this.hide();
        }
      }
    });
  },
  methods: {
    unlockText(po) {
      if (this.isSubscription) {
        if (this.trialEnabled) {
          return 'Start Trial';
        } else {
          return 'Subscribe';
        }
      } else if (this.isTieredPricing) {
        if (po.id === this.selectedPurchaseOrder.id) {
          return 'Selected';
        } else {
          return 'Select';
        }
      } else {
        return 'Unlock';
      }
    },
    isPreferred(po) {
      if (this.isSubscription && this.preferredPurchaseOrder.id) {
        return po.id === this.preferredPurchaseOrder.id;
      } else if (this.isTieredPricing) {
        return false;
      } else {
        return po.id === this.lastPurchaseOrder.id;
      }
    },
    isSelected(po) {
      if (this.isTieredPricing) {
        return po.id === this.selectedPurchaseOrder.id;
      } else if (this.isSubscription){
        return po.id === this.preferredPurchaseOrder.id;
      } else {
        return po.id === this.lastPurchaseOrder.id;
      }
    },
    boxClick(po, index) {
      if (this.isTieredPricing) {
        this.selected = po;
      } else {
        this.triggerSubmit(index);
      }
    },
    triggerSubmit(index) {
      let ref = this.$refs.submitForm[index].$el;
      ref.submit();
    },
    timeframe(po) {
      let str = null;
      switch(po.monthsUntilExpiration) {
        case 1:
          str = 'every month';
          break;
        case 12:
          str = 'every year';
          break;
        default:
          str = `every ${po.monthsUntilExpiration} months`;
      }
      return str;
    },
    price(po) {
      return +((po.webAppCost / 100).toFixed(2));
    },
    hide() {
      this.active = false;
    },
    injectCookies() {
      this.partnerRef = this.$cookie.get('partner_ref');
      this.sc = this.$cookie.get('sc');
    },
    toggle(params) {
      this.type = (params || {}).type || this.$store.getters['app/pricingType'];
      this.active = true;
      const source = this.$store.getters['ui/upsellModalSource'];
      this.$analytics.events.viewedUpgradeScreen(source);
      this.injectCookies();
      this.$store.dispatch('ui/toggleUpsellModal', { show: false, source: {} });
    },
  },
};
</script>

<style lang='scss' scoped>
  .upsell-modal-component-container {
    background-color: rgba(0,0,0,.7);
    bottom: 0;
    display: flex;
    left: 0;
    overflow-y: scroll;
    position: fixed;
    right: 0;
    top: 0;
    transition: opacity .3s ease;
    z-index: 1000;

    .content {
      background: white;
      border-radius: 10px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      margin: auto;
      max-width: rem-calc(1000);
      padding: rem-calc(32);
      position: relative;
      text-align: center;
      transition: transform .3s ease;
      width: 80%;

      @include breakpoint(medium) {
        width: 90%;
      }

      @include breakpoint(small) {
        border-radius: 0;
        min-height: 100%;
        width: 100%;
      }

      .close {
        cursor: pointer;
        left: rem-calc(8);
        position: absolute;
        top: rem-calc(8);

        img {
          height: rem-calc(32);
          width: rem-calc(32);
        }
      }

      .title {
        font-size: rem-calc(32);
        font-weight: $bold;

        @include breakpoint(small) {
          margin-top: rem-calc(16);
        }
      }

      .subtitle {
        font-size: rem-calc(20);
        margin-top: rem-calc(16);
      }

      .list /deep/ {
        > *:not(ul) {
          display: none;
        }

        ul {
          display: inline-block;
          font-size: rem-calc(18);
          list-style-type: none;
          margin: rem-calc(24);
          padding: 0 0 0 rem-calc(16);
          text-indent: rem-calc(-16);

          @include breakpoint(small) {
            margin: rem-calc(32) 0;
          }

          li {
            color: #212630 !important;
            margin-bottom: rem-calc(8);
            line-height: 2;
            text-align: left;
            width: 100%;

            span {
              color: #212630 !important;
            }

            &:before {
              content: '•';
              font-size: rem-calc(12);
              margin-right: rem-calc(16);
              vertical-align: text-top;
            }
          }

          // nest ul doesn't need margin
          ul {
            margin-bottom: 0 !important;
            margin-top: 0 !important;
            width: 100%;
          }
        }
      }

      .options {
        display: flex;
        justify-content: center;
        margin: auto auto 0 auto;
        padding-top: rem-calc(16);

        @include breakpoint(small) {
          margin: auto rem-calc(-32) 0 rem-calc(-32);
          padding: 0 rem-calc(12);
        }

        .spacer {
          width: rem-calc(26);

          @include breakpoint(small) {
            width: rem-calc(12);
          }
        }

        > div:not(.spacer) {
          background: white;
          border: 1px solid #e2e2e2;
          border-radius: 16px;
          cursor: pointer;
          padding: rem-calc(20) rem-calc(24);
          position: relative;
          text-align: center;
          width: rem-calc(150);
          z-index: 1;

          @include breakpoint(medium) {
            width: rem-calc(130);
          }

          @include breakpoint(small) {
            border-radius: 6px;
            flex: 1;
            padding: rem-calc(16) rem-calc(12);
            width: auto;
          }

          &:hover {
            box-shadow: 0 4px 6px 0 rgba(0, 0, 0, 0.24);
          }

          .name {
            font-size: rem-calc(20);
            font-weight: $bold;
            letter-spacing: 0.3px;

            @include breakpoint(small) {
              font-size: rem-calc(18);
              letter-spacing: 0px;
            }
          }

          .price {
            margin: rem-calc(32) 0;

            @include breakpoint(small) {
              margin-bottom: rem-calc(20);
            }

            > div {
              &:first-child {
                font-size: rem-calc(30);
                font-weight: $bold;

                @include breakpoint(small) {
                  font-size: rem-calc(28);
                }
              }

              &:nth-child(2) {
                font-size: rem-calc(16);

                @include breakpoint(small) {
                  font-size: rem-calc(12);
                }
              }
            }
          }

          form button {
            background: white;
            border: 1px solid #4fb9de;
            border-radius: 6px;
            color: #4fb9de;
            display: block;
            font-size: rem-calc(16);
            font-weight: $semi-bold;
            margin: auto;
            padding: rem-calc(10);
            width: 100%;

            &:not(.Tiered) {
              @include breakpoint(small) {
                display: none;
              }
            }
          }

          &.preferred {
            &::before {
              background: #4fb9de;
              border-top-left-radius: 6px;
              border-top-right-radius: 6px;
              bottom: calc(100% + 1px);
              color: white;
              content: 'Most Popular';
              font-size: rem-calc(12);
              font-weight: $semi-bold;
              left: rem-calc(16);
              padding: rem-calc(4) 0;
              position: absolute;
              right: rem-calc(16);
            }
          }

          &.selected {
            /deep/ form button {
              background: #4fb9de !important;
              color: white;
            }
          }
        }
      }

      .disclaimers {
        background: #f4f4f4;
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
        color: #8f8f8f;
        font-size: rem-calc(12);
        margin: rem-calc(-124) rem-calc(-32) rem-calc(-32) rem-calc(-32);
        padding: rem-calc(160) rem-calc(48) rem-calc(32) rem-calc(48);

        @include breakpoint(small) {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
          padding: rem-calc(80) rem-calc(32) rem-calc(16) rem-calc(32);
          margin: rem-calc(-64) rem-calc(-32) rem-calc(-32) rem-calc(-32);
        }

        button {
          background: #77d769;
          border-radius: 4px;
          box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.3);
          color: white;
          font-size: rem-calc(18);
          font-weight: $bold;
          max-width: rem-calc(300);
          padding: rem-calc(16);
          width: 100%;

          @include breakpoint(small) {
            margin: rem-calc(20) 0;
          }
        }

        > div {
          margin-bottom: rem-calc(12);

          &:first-child {
            color: black;
            font-size: rem-calc(14);

            &.semi-bold-disclaimer {
              font-size: rem-calc(18);
              font-weight: $semi-bold;
            }
          }
        }
      }
    }
  }
</style>
